import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

interface LocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  timestamp: number | null;
  loading: boolean;
  error: string | null;
  permission: 'granted' | 'denied' | 'prompt' | null;
}

interface UseLocationReturn extends LocationState {
  requestLocation: () => Promise<void>;
  clearLocation: () => void;
  watchLocation: () => void;
  stopWatching: () => void;
  isWatching: boolean;
}

export function useLocation(): UseLocationReturn {
  const [state, setState] = useState<LocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
    loading: false,
    error: null,
    permission: null,
  });
  const [isWatching, setIsWatching] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);

  useEffect(() => {
    // Check initial permission state if available
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        setState(prev => ({ ...prev, permission: result.state as any }));
      }).catch(() => {
        // Permission API not available, will be determined on request
      });
    }
  }, []);

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setState({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
      loading: false,
      error: null,
      permission: 'granted',
    });
  }, []);

  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = 'Unable to get location';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Please enable location in your device settings.';
        setState(prev => ({ ...prev, permission: 'denied' }));
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable. Please try again.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Please try again.';
        break;
    }

    setState(prev => ({
      ...prev,
      loading: false,
      error: errorMessage,
    }));
  }, []);

  const requestLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        loading: false,
      }));
      toast.error('Geolocation not supported');
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    } catch (error) {
      handleError({
        code: 2,
        message: 'Failed to get location',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      } as GeolocationPositionError);
    }
  }, [handleSuccess, handleError]);

  const watchLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    if (watchId !== null) {
      // Already watching
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    setIsWatching(true);

    const id = navigator.geolocation.watchPosition(
      (position) => {
        handleSuccess(position);
        setIsWatching(true);
      },
      handleError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000, // Accept position up to 5 seconds old
      }
    );

    setWatchId(id);
  }, [watchId, handleSuccess, handleError]);

  const stopWatching = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      setIsWatching(false);
    }
  }, [watchId]);

  const clearLocation = useCallback(() => {
    stopWatching();
    setState({
      latitude: null,
      longitude: null,
      accuracy: null,
      timestamp: null,
      loading: false,
      error: null,
      permission: state.permission,
    });
  }, [stopWatching, state.permission]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return {
    ...state,
    requestLocation,
    clearLocation,
    watchLocation,
    stopWatching,
    isWatching,
  };
}

// Utility function to calculate distance between two coordinates (Haversine formula)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// Format distance for display
export function formatDistance(miles: number): string {
  if (miles < 0.1) {
    return `${Math.round(miles * 5280)} ft`;
  } else if (miles < 1) {
    return `${(miles * 5280).toFixed(0)} ft`;
  } else {
    return `${miles.toFixed(1)} mi`;
  }
}

// Parse location string to coordinates
export function parseLocation(location: string): { lat: number; lon: number } | null {
  if (!location) return null;
  
  // Try to match common coordinate formats
  // Format 1: "34.0522, -118.2437" or "34.0522,-118.2437"
  const commaFormat = location.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
  if (commaFormat) {
    return {
      lat: parseFloat(commaFormat[1]),
      lon: parseFloat(commaFormat[2]),
    };
  }
  
  // Format 2: "34.0522 N, 118.2437 W"
  const degreeFormat = location.match(/(\d+\.?\d*)\s*[NS]\s*,?\s*(\d+\.?\d*)\s*[EW]/i);
  if (degreeFormat) {
    let lat = parseFloat(degreeFormat[1]);
    let lon = parseFloat(degreeFormat[2]);
    
    if (location.includes('S')) lat = -lat;
    if (location.includes('W')) lon = -lon;
    
    return { lat, lon };
  }
  
  return null;
}
